/**
 * Conversion soumission acceptée → facture (snapshot indépendant).
 * Décisions CDC: numéro FA-0001… ; échéance = date facture + 30 jours (Net 30).
 */

import { supabase } from './supabaseClient';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function addDaysIsoDate(isoDate, days) {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setTime(d.getTime() + days * MS_PER_DAY);
  return d.toISOString().slice(0, 10);
}

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function deepClone(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

/**
 * Returns the real sequential invoice number, or null if we can't reach the
 * server right now (offline, or a transient error). A null number means the
 * invoice is a provisional draft — the definitive number is assigned at
 * sync time (see lib/sync.js), never guessed locally. This guarantees the
 * sequence has no gaps and can never collide with another invoice.
 */
export async function getNextInvoiceNumber(userId) {
  if (!userId || !navigator.onLine) return null;
  try {
    const { data, error } = await supabase.rpc('next_invoice_number', { p_user_id: userId });
    if (error || !data) return null;
    return String(data);
  } catch {
    return null;
  }
}

/**
 * Build a new invoice object from an accepted submission (does not persist).
 * @returns {{ ok: true, invoice } | { ok: false, error: string }}
 */
export async function convertSubmissionToInvoice(sub, { userId, existingForSubmission } = {}) {
  if (!sub) return { ok: false, error: 'missing_submission' };
  if (sub.status !== 'accepted') return { ok: false, error: 'not_accepted' };
  if (existingForSubmission) {
    return { ok: false, error: 'already_converted', existing: existingForSubmission };
  }

  const ownerId = userId || sub.user_id;
  if (!ownerId) return { ok: false, error: 'missing_user' };

  const invoiceDate = todayIsoDate();
  const dueDate = addDaysIsoDate(invoiceDate, 30);
  const invoiceNumber = await getNextInvoiceNumber(ownerId);
  const now = new Date().toISOString();

  const invoice = {
    id: crypto.randomUUID(),
    user_id: ownerId,
    invoice_number: invoiceNumber,
    source_submission_id: sub.id || null,
    source_submission_number: sub.submission_number || null,
    status: 'draft',
    invoice_date: invoiceDate,
    due_date: dueDate,
    client_name: sub.client_name || '',
    client_address: sub.client_address || '',
    client_phone: sub.client_phone || '',
    client_email: sub.client_email || '',
    // Pas de champ chantier dédié aujourd'hui — on snapshot l'adresse client comme adresse de travaux.
    site_address: sub.client_address || '',
    work_type: sub.work_type || '',
    province: sub.province || 'QC',
    materials: deepClone(sub.materials) || [],
    labour: deepClone(sub.labour) || {},
    summary: deepClone(sub.summary) || {},
    notes: sub.notes || '',
    conditions: sub.conditions || '',
    synced: false,
    created_at: now,
    updated_at: now,
  };

  return { ok: true, invoice };
}
